package games.polyclub.website.frontend.store.layout.userarea

import games.polyclub.frontend.lib.react.bootstrap.components.Card
import games.polyclub.frontend.lib.react.simplified.SFC
import games.polyclub.frontend.lib.react.simplified.tags.*
import games.polyclub.frontend.lib.react.utils.useEffectOnceAsync
import games.polyclub.service.payment.api.Order
import games.polyclub.service.payment.api.PurchaseType
import games.polyclub.service.payment.api.id.Gateway
import games.polyclub.service.payment.api.id.OrderId
import games.polyclub.tool.functional.Option
import games.polyclub.tool.functional.retrying
import games.polyclub.tool.math.Decimal
import games.polyclub.tool.math.Decimal.Companion.dec
import games.polyclub.tool.strings.Currency
import games.polyclub.website.frontend.store.StoreBff
import games.polyclub.website.frontend.store.appScope
import games.polyclub.website.frontend.store.components.AsyncImg
import games.polyclub.website.frontend.store.components.Loading
import js.core.jso
import react.Props
import react.useState
import web.cssom.pct

external interface InvoiceDetailsComponentProps : Props {
    var paymentMethod: Gateway
    @Suppress("INLINE_CLASS_IN_EXTERNAL_DECLARATION_WARNING")
    var orderId: OrderId
}

val InvoiceDetailsComponent = SFC<InvoiceDetailsComponentProps> { props ->
    val (order, setOrder) = useState<Order?>(null)

    appScope.useEffectOnceAsync {
        setOrder(retrying {
            Option.of(StoreBff.readOriginalOrder(props.orderId))
        }.getOrNull())
    }

    if (order == null) {
        Loading()
        return@SFC
    }
    Card {
        title = "Dados do Pedido"
        table("table table-striped") {
            thead {
                tr {
                    th {
                        abbr{
                            title = "Quantidade"
                            +"Qtd"
                        }
                    }
                    th {
                        +"Produto"
                    }
                    th {
                        +"Para"
                    }
                    th {
                        abbr {
                            title = "Valor Unitário"
                            +"V.U."
                        }
                    }
                    if (order.totalTax.amount != 0.dec) {
                        th {
                            +"Taxas"
                        }
                    }
                    if (order.totalDiscount.amount != 0.dec) {
                        th {
                            +"Descontos"
                        }
                    }
                    th {
                        +"Subtotal"
                    }
                }
            }
            tbody {
                val currency = order.currency
                order.products.forEach { item ->
                    tr {
                        td {
                            +item.quantity.toString()
                        }
                        td {
                            +item.productName
                        }
                        td {
                            when (item.purchaseType) {
                                PurchaseType.GIVEAWAY -> strong {
                                    +(if (item.quantity == Decimal.ONE) "Ser sorteado" else "Serem sorteados")
                                }

                                PurchaseType.GIFT -> strong {
                                    AsyncImg("img-circle elevation-2 bg-secondary") {
                                        srcLoader = { StoreBff.avatarUrl(item.to.playerId.networkId) }
                                        width = 32.0
                                        height = 32.0
                                        alt = ""
                                    }
                                    +" ${item.to.playerName} "
                                    em {
                                        +"(Presente)"
                                    }
                                }

                                PurchaseType.NORMAL -> {
                                    AsyncImg("img-circle elevation-2 bg-secondary") {
                                        srcLoader = { StoreBff.avatarUrl(item.to.playerId.networkId) }
                                        width = 32.0
                                        height = 32.0
                                        alt = ""
                                    }
                                    +" ${item.to.playerName} "
                                }
                            }
                        }
                        td {
                            +item.basePrince.toString()
                        }
                        if (order.totalTax.amount != 0.dec) {
                            td {
                                if (item.taxes.none { it.affectsCustomer }) {
                                    +currency.amount(0).toString()
                                } else {
                                    dl {
                                        item.taxes.asSequence().filter { it.affectsCustomer }.forEach { tax ->
                                            dt {
                                                +tax.name
                                            }
                                            dd {
                                                var increase = tax.increase
                                                if (tax.perAmount) {
                                                    increase *= item.quantity
                                                }
                                                +increase.toString()
                                            }
                                        }
                                    }
                                }
                            }
                        }
                        if (order.totalDiscount.amount != 0.dec) {
                            td {
                                if (item.discounts.none { it.affectsCustomer }) {
                                    +currency.amount(0).toString()
                                } else {
                                    dl {
                                        item.discounts.asSequence().filter { it.affectsCustomer }.forEach { discount ->
                                            dt {
                                                +discount.name
                                            }
                                            dd {
                                                var reduction = discount.reduction
                                                if (discount.perAmount) {
                                                    reduction *= item.quantity
                                                }
                                                +reduction.toString()
                                            }
                                        }
                                    }
                                }
                            }
                        }
                        td {
                            +item.total.toString()
                        }
                    }
                }
            }
        }
        div("row") {
            div("col-6") {
                p("lead") {
                    +"Método de Pagamento"
                }
                div("row") {
                    div("col") {
                        when (props.paymentMethod) {
                            Gateway.NUBANK, Gateway.PAG_SEGURO -> img("mb-3") {
                                height = 32.0
                                src = "/img/logo-pix.svg"
                                alt = ""
                            }

                            Gateway.PICPAY -> img("mb-3") {
                                height = 32.0
                                src = "/img/logo-picpay.png"
                                alt = ""
                            }

                            Gateway.PAYPAL, Gateway.PAYPAL_INVOICE -> img("mb-3") {
                                height = 32.0
                                src = "/img/logo-paypal.png"
                                alt = ""
                            }
                        }
                    }
                }
                div("row") {
                    div("col") {
                        if (order.currency == Currency.BRL) {
                            p {
                                +"Como você mora no Brasil, somente transações em Reais são permitidas."
                            }
                            p {
                                +"No Brasil aceitamos Pix ou os métodos fornecidos pelo PicPay."
                            }
                            p{
                                strong {
                                    +"Nota: "
                                }
                                +"Estamos temporariamente aceitando apenas os métodos fornecidos PayPal, inclusive para brasileiros."
                            }
                        } else {
                            p {
                                +"Como você mora fora do Brasil, somente transações em Euro são permitidas."
                            }
                            p {
                                +"Fora do Brasil aceitamos apenas os métodos fornecidos pelo PayPal."
                            }
                        }
                    }
                }
            }
            div("col-6") {
                p("lead") {
                    +"Totais"
                }
                div("table-responsive") {
                    table("table") {
                        tbody {
                            tr {
                                th {
                                    style = jso {
                                        width = 50.pct
                                    }
                                    +"Subtotal:"
                                }
                                td {
                                    +order.fullBasePrice.toString()
                                }
                            }
                            tr {
                                th {
                                    +"Taxas:"
                                }
                                td {
                                    +order.totalTax.toString()
                                }
                            }
                            tr {
                                th {
                                    +"Descontos:"
                                }
                                td {
                                    +order.totalDiscount.toString()
                                }
                            }
                            tr {
                                th {
                                    +"Total:"
                                }
                                td {
                                    +order.total.toString()
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}
